// Settings sidebar-takeover (Linear-style) styles (WEB-8003). AgentConfig
// feature styles (WEB-7961 folder). Composed by workspaceStyles.ts in place.
export const agentConfigSettingsTakeover = `
/* ===== Settings: sidebar takeover (Linear-style) =====
   While route === 'settings' the workspace sidebar is swapped for this rail,
   reusing the sidebar's own visual language (ws-sb-item / ws-sb-section-label)
   so it reads as the same surface, never as a third menu. */
.ws-settings-sidebar .ws-sb-scrollable {
  padding-top: 14px;
}
.ws-settings-back {
  color: var(--ws-ink-3);
  margin-bottom: 4px;
}
.ws-settings-nav {
  display: flex;
  flex-direction: column;
}
.ws-settings-nav-group {
  margin-top: 14px;
}
/* Body content: consistent breathing room below the topbar — every tab gets
   the same gap the Skills card used to carry as its own margin. */
.ws-settings-body {
  padding-top: 24px;
}

/* Usage pane toolbar — scope toggle + period nav share one row. */
.ws-usage-pane-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}
.ws-usage-pane-toolbar .ws-admin-period-nav { margin-left: 0; }

/* Segmented scope toggle (You / Organisation), reused on the org page. */
.ws-scope-toggle {
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  background: var(--ws-hover);
  border: 1px solid var(--ws-line);
  border-radius: 8px;
}
.ws-scope-toggle-btn {
  padding: 5px 14px;
  border: 0;
  background: transparent;
  border-radius: 6px;
  font-family: var(--ws-sans);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ws-ink-4);
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.ws-scope-toggle-btn:hover { color: var(--ws-ink-2); }
.ws-scope-toggle-btn.is-active {
  background: white;
  color: var(--ws-ink);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.ws-admin-card {
  background: white;
  border: 1px solid var(--ws-line);
  border-radius: 10px;
  padding: 18px 20px;
  margin-bottom: 18px;
}
.ws-admin-card-head {
  /* Row layout: title left, header actions (buttons/toggles) right — Skills, Connections,
     Slack and Users all rely on this. */
  display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
  margin-bottom: 14px;
}
.ws-admin-card-head--stacked {
  /* Variant for cards whose head is title + descriptive text only: stack the description
     under the title instead of flinging it to the far right of the card. */
  flex-direction: column; align-items: flex-start; gap: 2px;
}
.ws-admin-card-head h3 {
  margin: 0;
  font-size: 14px; font-weight: 600;
  color: var(--ws-ink);
}
.ws-admin-card .ws-sub { font-size: 12px; color: var(--ws-ink-4); }
.ws-admin-empty {
  padding: 22px 4px;
  font-size: 13px;
  color: var(--ws-ink-4);
  text-align: center;
}

/* Hero */
.ws-admin-hero {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 28px;
  align-items: center;
  background: white;
  border: 1px solid var(--ws-line);
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 18px;
}
.ah-side .ah-label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--ws-ink-4); font-weight: 600;
}
.ah-side .ah-value {
  font-family: var(--ws-serif);
  font-size: 44px; font-weight: 600; letter-spacing: -0.01em;
  color: var(--ws-ink);
  margin-top: 4px; line-height: 1;
}
.ah-side .ah-of { font-size: 13px; color: var(--ws-ink-4); margin-top: 6px; }
.ah-bar {
  margin-top: 14px;
  height: 4px;
  background: var(--ws-line);
  border-radius: 999px;
  overflow: hidden;
}
.ah-bar-fill {
  height: 100%;
  background: var(--ws-ink);
}

/* Credit-pool hero styling — same scaffold, status-coloured fill so a
   glance tells you whether the account is over, near, or under cap. */
.ws-credit-hero { background: var(--ws-panel); }
.ws-credit-bar-fill--ok { background: var(--ws-ok, #2f7a4e); }
.ws-credit-bar-fill--warn { background: var(--ws-warn, #cf8c34); }
.ws-credit-bar-fill--over { background: var(--ws-err, #b23434); }

/* Soft-warn banner — informational, never blocks the agent. */
.ws-credit-warn {
  display: flex; align-items: center; gap: 14px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid;
  margin-bottom: 14px;
  font-size: 13px;
}
.ws-credit-warn--warn {
  background: rgba(207, 140, 52, 0.08);
  border-color: rgba(207, 140, 52, 0.30);
  color: var(--ws-warn, #cf8c34);
}
.ws-credit-warn--over {
  background: rgba(178, 52, 52, 0.06);
  border-color: rgba(178, 52, 52, 0.28);
  color: var(--ws-err, #b23434);
}
.ws-credit-warn-text { flex: 1; }
.ws-credit-warn-cta {
  font-size: 12px; font-weight: 600;
  padding: 6px 12px; border-radius: 4px;
  border: 1px solid currentColor;
  background: transparent; color: inherit;
  cursor: pointer;
}
.ws-credit-warn-cta:hover { background: currentColor; color: var(--ws-panel); }

/* Per-tool breakdown — one row per feature, sorted server-side by
   creditsSek desc, bar normalised against the heaviest tool. */
.ws-credit-tools { display: flex; flex-direction: column; gap: 8px; }
.ws-credit-tool-row {
  display: grid;
  grid-template-columns: minmax(140px, 1.5fr) 3fr auto;
  align-items: center;
  gap: 14px;
  padding: 6px 0;
}
.ws-credit-tool-name {
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 12px;
  color: var(--ws-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ws-credit-tool-bar {
  height: 8px;
  background: var(--ws-line);
  border-radius: 999px;
  overflow: hidden;
}
.ws-credit-tool-fill {
  height: 100%;
  background: var(--ws-ink);
  border-radius: 999px;
}
.ws-credit-tool-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  min-width: 90px;
}
.ws-credit-tool-credits { font-weight: 600; color: var(--ws-ink); }
.ws-credit-tool-calls { color: var(--ws-ink-5); font-size: 11px; }

/* Admin Usage breakdown — list view with group-by selector + filter. */
.ws-usage-toolbar {
  display: flex; align-items: center; gap: 12px;
  flex-wrap: wrap;
}
.ws-usage-groupby {
  display: inline-flex;
  background: var(--ws-soft);
  border: 1px solid var(--ws-line);
  border-radius: 6px;
  padding: 2px;
}
.ws-usage-groupby-btn {
  border: 0;
  background: transparent;
  padding: 4px 12px;
  font-size: 12.5px;
  color: var(--ws-ink-3);
  cursor: pointer;
  border-radius: 4px;
}
.ws-usage-groupby-btn:hover { color: var(--ws-ink); }
.ws-usage-groupby-btn.is-active {
  background: var(--ws-panel);
  color: var(--ws-ink);
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(15, 30, 47, 0.06);
}
.ws-usage-filter {
  flex: 1;
  min-width: 180px;
  padding: 6px 10px;
  border: 1px solid var(--ws-line);
  border-radius: 6px;
  font-size: 13px;
}
.ws-usage-filter:focus { outline: none; border-color: var(--ws-ink-3); }

.ws-usage-list { display: flex; flex-direction: column; gap: 6px; }
.ws-usage-row {
  padding: 8px 0;
  border-bottom: 1px solid var(--ws-line);
}
.ws-usage-row:last-child { border-bottom: 0; }
.ws-usage-row.is-clickable { cursor: pointer; }
.ws-usage-row.is-clickable:hover { background: rgba(15, 30, 47, 0.02); }
.ws-usage-row-main {
  display: grid;
  grid-template-columns: minmax(160px, 1.6fr) 3fr auto;
  align-items: center;
  gap: 14px;
}
.ws-usage-row-label { min-width: 0; }
.ws-usage-row-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--ws-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ws-usage-row-coverage-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--ws-ink-4);
  background: var(--ws-soft);
  border: 1px solid var(--ws-line);
  border-radius: 999px;
  text-transform: uppercase;
}
.ws-usage-row-sub {
  font-size: 11.5px;
  color: var(--ws-ink-5);
  margin-top: 2px;
}
.ws-usage-row-bar {
  height: 8px;
  background: var(--ws-line);
  border-radius: 999px;
  overflow: hidden;
}
.ws-usage-row-bar-fill {
  height: 100%;
  background: var(--ws-ink);
  border-radius: 999px;
}
.ws-usage-row-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-variant-numeric: tabular-nums;
  font-size: 12.5px;
  min-width: 110px;
}
.ws-usage-row-credits { font-weight: 600; color: var(--ws-ink); }
.ws-usage-row-calls { color: var(--ws-ink-5); font-size: 11px; }
.ws-usage-row-detail {
  padding: 8px 0 4px 0;
  font-size: 12.5px;
  color: var(--ws-ink-4);
  line-height: 1.5;
}

/* Category expansion — list of constituent features under a category row. */
.ws-usage-row-details {
  padding: 8px 0 4px 16px;
  display: flex; flex-direction: column; gap: 6px;
  border-left: 2px solid var(--ws-line);
  margin-left: 6px;
}
.ws-usage-row-detail-row {
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 12px;
  font-size: 12.5px;
}
.ws-usage-row-detail-label { min-width: 0; }
.ws-usage-row-detail-name { font-weight: 500; color: var(--ws-ink-2); }
.ws-usage-row-detail-desc { font-size: 11.5px; color: var(--ws-ink-5); margin-top: 2px; }
.ws-usage-row-detail-meta {
  display: flex; flex-direction: column; align-items: flex-end;
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  min-width: 90px;
}

/* Daily bar chart */
.ws-usage-chart {
  display: flex; flex-direction: column; gap: 6px;
  background: var(--ws-panel);
  border: 1px solid var(--ws-line);
  border-radius: 6px;
  padding: 10px 12px;
}
.ws-usage-chart-svg {
  width: 100%;
  height: 80px;
  display: block;
}
.ws-usage-chart-bar {
  fill: oklch(0.60 0.13 250);
  fill-opacity: 0.78;
  transition: fill-opacity 0.12s, fill 0.12s;
  cursor: pointer;
}
.ws-usage-chart-bar.is-hover,
.ws-usage-chart-bar:hover { fill: oklch(0.50 0.16 250); fill-opacity: 1; }
.ws-usage-chart-meta {
  font-size: 12px;
  color: var(--ws-ink);
  min-height: 16px;
}
.ws-usage-chart-empty {
  padding: 24px;
  text-align: center;
  color: var(--ws-ink-5);
  font-size: 13px;
  border: 1px dashed var(--ws-line);
  border-radius: 6px;
}
.ws-usage-chart.is-loading {
  opacity: 0.55;
  transition: opacity 0.2s;
}

/* Expandable filter panel below the toolbar */
.ws-usage-filter-panel {
  display: flex; flex-direction: column; gap: 8px;
  padding: 10px 0 0 0;
  border-top: 1px dashed var(--ws-line);
  margin-top: 4px;
}

/* Multiselect popover (Users / Agents) */
.ws-usage-multiselect-trigger {
  display: inline-flex; align-items: center; gap: 6px;
}
.ws-usage-multiselect-popover {
  width: 280px;
  padding: 8px;
  display: flex; flex-direction: column; gap: 6px;
}
.ws-usage-multiselect-search {
  width: 100%;
  padding: 5px 8px;
  border: 1px solid var(--ws-line);
  border-radius: 4px;
  font-size: 12.5px;
}
.ws-usage-multiselect-search:focus { outline: none; border-color: var(--ws-ink-3); }
.ws-usage-multiselect-list {
  display: flex; flex-direction: column;
  max-height: 240px;
  overflow-y: auto;
  gap: 0;
}
.ws-usage-multiselect-empty {
  padding: 12px;
  font-size: 12px;
  color: var(--ws-ink-5);
  text-align: center;
}
.ws-usage-multiselect-item {
  display: flex; align-items: flex-start; gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  border-radius: 4px;
}
.ws-usage-multiselect-item:hover { background: var(--ws-soft); }
.ws-usage-multiselect-item.is-checked { background: rgba(15, 30, 47, 0.04); }
.ws-usage-multiselect-check {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px;
  border: 1px solid var(--ws-line);
  border-radius: 3px;
  background: var(--ws-panel);
  margin-top: 1px;
  flex-shrink: 0;
}
.ws-usage-multiselect-item.is-checked .ws-usage-multiselect-check {
  background: var(--ws-ink);
  border-color: var(--ws-ink);
  color: var(--ws-panel);
}
.ws-usage-multiselect-label { display: flex; flex-direction: column; min-width: 0; }
.ws-usage-multiselect-name { font-size: 12.5px; color: var(--ws-ink); }
.ws-usage-multiselect-sub { font-size: 11px; color: var(--ws-ink-5); }
.ws-usage-multiselect-clear {
  border: 0;
  background: transparent;
  color: var(--ws-ink-4);
  font-size: 11.5px;
  cursor: pointer;
  text-align: left;
  padding: 4px 8px;
}
.ws-usage-multiselect-clear:hover { color: var(--ws-ink); }

/* Collapse-LLM toggle */
.ws-usage-collapse-toggle {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; color: var(--ws-ink-3);
  cursor: pointer;
  margin-left: auto;
}
.ws-usage-collapse-toggle input { margin: 0; cursor: pointer; }
.ah-bar-meta {
  margin-top: 8px;
  font-size: 12px;
  color: var(--ws-ink-4);
  display: flex; gap: 8px;
}
.ah-spark { min-width: 0; }

/* KPI strip */
.ws-admin-kpis {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 18px;
}
.ws-admin-kpis .kpi {
  background: white;
  border: 1px solid var(--ws-line);
  border-radius: 10px;
  padding: 14px 16px;
  min-width: 0;
}
.kpi-label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--ws-ink-4); font-weight: 600;
}
.kpi-value-row { display: flex; align-items: center; gap: 8px; margin-top: 6px; min-width: 0; }
.kpi-value {
  font-family: var(--ws-serif);
  font-size: 26px; font-weight: 600;
  color: var(--ws-ink);
  line-height: 1;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.kpi-value.ok { color: var(--ws-ok); }
.kpi-value.warn { color: var(--ws-warn); }
.kpi-value.err { color: var(--ws-err); }
.kpi-sub { font-size: 11.5px; color: var(--ws-ink-4); margin-top: 4px; }
.ws-admin-kpis .kpi.is-clickable { cursor: pointer; transition: border-color 0.12s, box-shadow 0.12s; }
.ws-admin-kpis .kpi.is-clickable:hover {
  border-color: var(--ws-ink-4);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

/* Two-column grid */
.ws-admin-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.ws-admin-list {
  display: flex; flex-direction: column;
  gap: 10px;
}
/* "Show more / show less" expander under capped overview lists */
.ws-admin-list-more {
  border: 0; background: none;
  padding: 8px 4px 2px;
  font-size: 12px; font-weight: 500;
  color: var(--ws-ink-4);
  cursor: pointer;
  text-align: center;
}
.ws-admin-list-more:hover { color: var(--ws-ink); }

/* User spend rows */
.user-spend-row,
.agent-usage-row {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid var(--ws-line);
}
.user-spend-row:last-child,
.agent-usage-row:last-child { border-bottom: 0; }
.user-spend-row.is-clickable,
.agent-usage-row.is-clickable { cursor: pointer; border-radius: 8px; }
.user-spend-row.is-clickable:hover,
.agent-usage-row.is-clickable:hover { background: var(--ws-soft); }
.usr-avatar {
  width: 32px; height: 32px;
  border-radius: 999px;
  display: grid; place-items: center;
  font-family: var(--ws-serif);
  font-size: 12px; font-weight: 700;
  color: white;
  flex-shrink: 0;
}
.usr-name { font-weight: 500; color: var(--ws-ink); font-size: 13px; }
.usr-role { font-size: 11.5px; color: var(--ws-ink-4); }
.usr-bars { display: flex; flex-direction: column; gap: 4px; min-width: 220px; }
.usr-bar-row { display: flex; align-items: center; gap: 8px; font-size: 11.5px; }
.usr-bar-label { color: var(--ws-ink-4); width: 80px; flex-shrink: 0; }
.usr-bar {
  flex: 1; height: 4px;
  background: var(--ws-line);
  border-radius: 999px;
  overflow: hidden;
}
.usr-bar-fill { height: 100%; }
.usr-bar-val {
  font-variant-numeric: tabular-nums;
  font-size: 11.5px; color: var(--ws-ink-3);
  width: 110px; text-align: right;
}
.usr-total {
  font-family: var(--ws-serif);
  font-size: 18px; font-weight: 600;
  color: var(--ws-ink);
  font-variant-numeric: tabular-nums;
  width: 80px; text-align: right;
}

/* Success badges */
.success-badge {
  width: 64px; flex-shrink: 0;
  text-align: center;
  padding: 8px 0;
  border-radius: 8px;
  background: var(--ws-soft);
}
.success-badge.ok { color: var(--ws-ok); background: rgba(47,122,78,0.08); }
.success-badge.warn { color: var(--ws-warn); background: rgba(184,112,42,0.08); }
.success-badge.err { color: var(--ws-err); background: rgba(184,74,62,0.08); }
.success-badge .sb-num { font-family: var(--ws-serif); font-size: 16px; font-weight: 700; line-height: 1; }
.success-badge .sb-lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.85; }

/* Pills used in admin */
.ws-pill {
  display: inline-flex; align-items: center;
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.02em;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--ws-soft);
  color: var(--ws-ink-3);
  border: 1px solid var(--ws-line);
}
.ws-pill.dark {
  background: var(--ws-ink); color: #e8f9f8; border-color: var(--ws-ink);
}
.ws-pill.warn {
  background: rgba(184,112,42,0.12); color: var(--ws-warn); border-color: rgba(184,112,42,0.25);
}

/* Tables */
.ws-admin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}
.ws-admin-table th {
  text-align: left;
  padding: 8px 10px;
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em;
  font-weight: 600; color: var(--ws-ink-4);
  border-bottom: 1px solid var(--ws-line);
  /* Long single-word headers (e.g. Swedish compounds) must wrap inside the
     fixed-width column instead of overflowing into the next cell. */
  overflow-wrap: anywhere;
}
.ws-admin-table td {
  padding: 12px 10px;
  border-bottom: 1px solid var(--ws-line);
  vertical-align: middle;
  color: var(--ws-ink-2);
}
.ws-admin-table tr:last-child td { border-bottom: 0; }

/* WEB-7089 — runs audit table */
.ws-runs-row.is-clickable { cursor: pointer; }
.ws-runs-row.is-clickable:hover td { background: var(--ws-soft); }
.ws-runs-date {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--ws-ink-4);
}
.ws-runs-pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 10px 0;
}

/* Promotion cards */
.ws-promotion-card {
  background: linear-gradient(180deg, white, var(--ws-soft));
  border: 1px solid var(--ws-line);
  border-radius: 10px;
  padding: 18px 20px;
}
.ws-promotion-head { display: flex; align-items: center; gap: 12px; }
.ws-promotion-name { font-weight: 600; color: var(--ws-ink); font-size: 14px; }
.ws-promotion-quote {
  margin: 12px 0 0;
  padding: 10px 14px;
  font-family: var(--ws-serif);
  font-style: italic;
  font-size: 14px;
  color: var(--ws-ink-2);
  border-left: 2px solid var(--ws-ink);
  background: white;
  border-radius: 0 6px 6px 0;
}
.ws-promotion-stats {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.ws-promotion-stats .ps-num {
  font-family: var(--ws-serif);
  font-size: 18px; font-weight: 600;
  color: var(--ws-ink);
  font-variant-numeric: tabular-nums;
}
.ws-promotion-stats .ps-lbl {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--ws-ink-4);
  margin-top: 2px;
}
.ws-promotion-actions {
  margin-top: 14px;
  display: flex;
  gap: 8px;
  align-items: center;
}

/* Promotion threshold config */
.ws-admin-threshold-row {
  display: flex; gap: 16px; align-items: flex-end;
  padding: 0 20px 20px;
}
.ws-admin-threshold-field {
  display: flex; flex-direction: column; gap: 5px; flex: 1;
}
.ws-admin-threshold-field label {
  font-size: 12px; font-weight: 500; color: var(--ws-ink-3);
}

/* Limits grid */
.ws-admin-limits-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.ws-admin-limit-card {
  padding: 16px;
  background: var(--ws-soft);
  border: 1px solid var(--ws-line);
  border-radius: 8px;
}
.ws-admin-limit-label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--ws-ink-4); font-weight: 600;
}
.ws-admin-limit-input-row {
  display: flex; align-items: center; gap: 4px; margin-top: 4px;
}
.ws-admin-limit-dollar {
  font-family: var(--ws-serif); font-size: 20px; font-weight: 600; color: var(--ws-ink-3);
}
.ws-admin-limit-input {
  font-family: var(--ws-serif); font-size: 20px; font-weight: 600; color: var(--ws-ink);
  background: var(--ws-bg); border: 1px solid var(--ws-line); border-radius: 6px;
  padding: 2px 8px; width: 120px; outline: none;
}
.ws-admin-limit-input:focus { border-color: var(--ws-ink-3); }
.ws-admin-limit-input-err { border-color: var(--ws-err); }
.ws-admin-limit-mo {
  font-size: 13px; color: var(--ws-ink-4); margin-left: 2px;
}
.ws-admin-limit-sub { font-size: 11.5px; color: var(--ws-ink-4); margin-top: 4px; }
.ws-admin-limit-err {
  margin-top: 6px; font-size: 11.5px; font-weight: 500;
  color: var(--ws-err); line-height: 1.4;
}

@media (max-width: 1100px) {
  .ws-admin-grid-2 { grid-template-columns: 1fr; }
  .ws-admin-kpis { grid-template-columns: repeat(2, 1fr); }
  .ws-admin-limits-grid { grid-template-columns: 1fr; }
  .ws-admin-hero { grid-template-columns: 1fr; }
}
`;
